import { BaseProjectType, ChangeOrderStatusEnum, ChangeOrderType } from 'types/projects';
import { fetchRequest } from 'util/fetch';
import { urlToId } from './misc';

/** Project fields refreshed when opening Manage → Sale after proposal acceptance. */
export const SALE_DATA_SYNC_FIELDS = [
  'change_orders',
  'initiated_change_order',
  'original_system_sold',
  'system_sold',
  'payment_option_sold',
] as const

const ACTIVE_CHANGE_ORDER_STATUSES = new Set([ChangeOrderStatusEnum.DRAFT, ChangeOrderStatusEnum.PUBLISHED])

/** Project fields read by change-order helpers; forms and tests may supply partial shapes. */
export type ChangeOrderProjectValues = {
  id?: number
  project_sold?: BaseProjectType['project_sold']
  contract_date?: BaseProjectType['contract_date'] | string | null
  system_sold?: BaseProjectType['system_sold'] | string
  payment_option_sold?: BaseProjectType['payment_option_sold'] | string | null
  change_orders?: Array<ChangeOrderType | null | undefined>
  /** API id or in-memory change-order object while editing. */
  initiated_change_order?: number | ChangeOrderType | null
  original_system_sold?: BaseProjectType['original_system_sold']
  systems?: BaseProjectType['systems']
}

export const isCompletedChangeOrder = (changeOrder: ChangeOrderType): boolean => {
  return changeOrder.is_complete || changeOrder.status === ChangeOrderStatusEnum.COMPLETED
}

export const isActiveChangeOrder = (changeOrder: ChangeOrderType): boolean => {
  if (changeOrder.status) {
    return ACTIVE_CHANGE_ORDER_STATUSES.has(changeOrder.status)
  }
  return !isCompletedChangeOrder(changeOrder)
}

export const isIncompleteChangeOrder = (changeOrder: ChangeOrderType): boolean => {
  if (changeOrder.status === ChangeOrderStatusEnum.COMPLETED || changeOrder.status === ChangeOrderStatusEnum.CANCELLED) {
    return false
  }
  return !changeOrder.is_complete
}

export const isPublishedChangeOrder = (changeOrder: ChangeOrderType): boolean => {
  return changeOrder.status === ChangeOrderStatusEnum.PUBLISHED
}

/** MUI Select returns string values; API expects numeric system ids. */
export const normalizeChangeOrderSelectedSystemIds = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return []
  const ids = raw
    .map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v)))
    .filter((n) => !Number.isNaN(n))
  return [...new Set(ids)]
}

/** ReferenceInput may store a resource URL; publish endpoint expects a numeric template id. */
export const normalizeChangeOrderDocumentTemplateId = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  const fromUrl = urlToId(String(value))
  if (fromUrl) return Number(fromUrl)
  const parsed = Number(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

export const getChangeOrderPublishPayload = (changeOrder: ChangeOrderType) => ({
  selectedSystems: normalizeChangeOrderSelectedSystemIds(changeOrder.selected_systems),
  documentTemplateId: normalizeChangeOrderDocumentTemplateId(changeOrder.document_template),
})

export const isChangeOrderReadyToPublish = (changeOrder: ChangeOrderType): boolean => {
  if (isPublishedChangeOrder(changeOrder)) return true
  const { selectedSystems, documentTemplateId } = getChangeOrderPublishPayload(changeOrder)
  return !!documentTemplateId && selectedSystems.length > 0
}

export const getChangeOrderProposalPath = (projectId: number | string) =>
  `/projects/${projectId}/proposal?include_unsold=1`

/** Bust MyEnergy proposal cache when change-order selection or lifecycle changes. */
export const buildChangeOrderProposalCacheSnapshot = (projectValues: ChangeOrderProjectValues | undefined) => {
  if (!projectValues) {
    return {}
  }
  const orders = getChangeOrders(projectValues)
  const initiated = projectValues.initiated_change_order as ChangeOrderType | null | undefined
  return {
    change_orders: orders.map((co) => ({
      id: co.id,
      status: co.status,
      is_complete: co.is_complete,
      selected_systems: normalizeChangeOrderSelectedSystemIds(co.selected_systems),
    })),
    initiated_change_order_id: initiated?.id ?? null,
    initiated_change_order_selected_systems: initiated
      ? normalizeChangeOrderSelectedSystemIds(initiated.selected_systems)
      : [],
  }
}

export const hasActiveChangeOrder = (projectValues: ChangeOrderProjectValues): boolean => {
  const activeChangeOrders = getActiveChangeOrders(projectValues)
  return activeChangeOrders.length > 0
}

/** True when the project has any change order record or initiated CO payload on the form. */
export const hasAnyChangeOrder = (projectValues: ChangeOrderProjectValues): boolean => {
  return getChangeOrders(projectValues).length > 0 || !!projectValues.initiated_change_order
}

/**
 * Get the active change orders for a project.
 * For how the 'active' is determined, see isActiveChangeOrder for more details.
 * @see isActiveChangeOrder for more details.
 */
export const getActiveChangeOrders = (projectValues: ChangeOrderProjectValues): ChangeOrderType[] => {
  return getChangeOrders(projectValues).filter(isActiveChangeOrder)
}

export const getCompletedChangeOrders = (projectValues: ChangeOrderProjectValues): ChangeOrderType[] => {
  return getChangeOrders(projectValues).filter(isCompletedChangeOrder)
}

/** Newest first: contract_date descending, then id descending when dates tie. */
export const compareChangeOrdersByRecency = (a: ChangeOrderType, b: ChangeOrderType): number => {
  const dateDiff = new Date(b.contract_date ?? '').getTime() - new Date(a.contract_date ?? '').getTime()
  if (dateDiff !== 0) return dateDiff
  return b.id - a.id
}

export const getMostRecentCompletedChangeOrder = (
  projectValues: ChangeOrderProjectValues
): ChangeOrderType | undefined => {
  const completed = getCompletedChangeOrders(projectValues)
  if (completed.length === 0) return undefined
  return [...completed].sort(compareChangeOrdersByRecency)[0]
}

/**
 * Lock Manage / Sale top-row sold fields permanently after the first change order.
 * Requires sold fields to be filled (same gate as V2 change-order UI).
 */
export const areTopRowSoldFieldsLocked = (projectValues: ChangeOrderProjectValues): boolean => {
  const hasFilledAllSoldFields = Boolean(
    projectValues.project_sold &&
    projectValues.contract_date &&
    projectValues.system_sold &&
    projectValues.payment_option_sold
  )
  if (!hasFilledAllSoldFields) {
    return false
  }
  return hasAnyChangeOrder(projectValues)
}

const addSoldSystemId = (previouslySoldIds: Set<number>, value: unknown): void => {
  if (value == null || value === '') return
  if (typeof value === 'number' && !Number.isNaN(value)) {
    previouslySoldIds.add(value)
    return
  }
  const id = urlToId(String(value))
  if (id) previouslySoldIds.add(Number(id))
}

export const getPreviouslySoldSystemIds = (projectValues: ChangeOrderProjectValues): Set<number> => {
  const previouslySoldIds = new Set<number>()

  if (projectValues.original_system_sold) {
    addSoldSystemId(previouslySoldIds, projectValues.original_system_sold)
    addSoldSystemId(previouslySoldIds, projectValues.system_sold)
  } else {
    addSoldSystemId(previouslySoldIds, projectValues.system_sold)
  }

  getChangeOrders(projectValues).forEach((changeOrder) => {
    const isCompleted = changeOrder.is_complete || changeOrder.status === ChangeOrderStatusEnum.COMPLETED
    if (isCompleted) {
      addSoldSystemId(previouslySoldIds, changeOrder.system_sold)
    }
  })

  return previouslySoldIds
}

/**
 * Get the number of systems that are eligible for a change order.
 * This is the number of systems that are not previously sold and are shown to the customer.
 */
export const getEligibleChangeOrderSystemCount = (projectValues: ChangeOrderProjectValues): number => {
  const previouslySoldIds = getPreviouslySoldSystemIds(projectValues)
  return (projectValues.systems ?? []).filter(
    (system) => system?.id != null && !previouslySoldIds.has(Number(system.id)) && system.show_customer !== false
  ).length
}

type PublishChangeOrderParams = {
  orgId: number
  projectId: number
  changeOrderId: number
  selectedSystems: number[]
  documentTemplateId: number
  restClientInstance: (
    type: string,
    resource: string,
    params: Record<string, unknown>
  ) => Promise<{ data: ChangeOrderType }>
}

export const publishChangeOrder = ({
  orgId,
  projectId,
  changeOrderId,
  selectedSystems,
  documentTemplateId,
  restClientInstance,
}: PublishChangeOrderParams) => {
  return restClientInstance('CUSTOM_POST', 'custom', {
    url: `orgs/${orgId}/projects/${projectId}/change_orders/${changeOrderId}/publish/`,
    data: {
      selected_systems: selectedSystems,
      document_template: documentTemplateId,
    },
  })
}

export const deleteInitiatedChangeOrder = (
  initiatedChangeOrder: number,
  orgId: number,
  projectId: number,
  restClientInstance,
  form,
  onSuccess?: () => void
) => {
  if (initiatedChangeOrder) {
    restClientInstance('CUSTOM_DELETE', 'custom', {
      url: `orgs/${orgId}/projects/${projectId}/change_orders/?change_order_id=${initiatedChangeOrder}`,
    }).then(() => {
      form.mutators.updateFieldSilently(
        'change_orders',
        form.getState().values.change_orders.filter((x) => !!x && x.id !== initiatedChangeOrder)
      )
      form.mutators.updateFieldSilently('initiated_change_order', null)
      onSuccess?.()
    })
  }
}

export const getChangeOrders = (projectValues: ChangeOrderProjectValues, form?) => {
  if (!projectValues || !projectValues.change_orders || projectValues.change_orders.length === 0) {
    return []
  }

  const changeOrders = projectValues.change_orders.filter((v): v is ChangeOrderType => !!v)
  if (form) {
    form.mutators.updateFieldSilently('change_orders', changeOrders)
  }

  return changeOrders
}

/** 1-based list position matching Change Order accordion titles (e.g. "Change Order #1"). */
export const getChangeOrderDisplayNumber = (
  projectValues: ChangeOrderProjectValues,
  changeOrderId: number
): number | undefined => {
  const index = (projectValues.change_orders ?? []).findIndex((item) => item?.id === changeOrderId)
  return index >= 0 ? index + 1 : undefined
}

export const getInitiatedChangeOrder = (projectValues: ChangeOrderProjectValues) => {
  const changeOrders = getChangeOrders(projectValues)
  return changeOrders.find(isActiveChangeOrder)
}

/** Change-order fields used when resolving per-system contract HTML in checkout. */
export type ChangeOrderContractSource = {
  system_change_orders?: { [key: string]: { change_order_content?: string | null } }
  change_order_template_content?: string | null
}

/** Per-system rendered change-order contract HTML, when present on the proposal payload. */
export const getChangeOrderContractContent = (
  changeOrder: ChangeOrderContractSource | null | undefined,
  systemId: number | null | undefined
): string | undefined => {
  if (!changeOrder || systemId == null) {
    return undefined
  }
  const systemChangeOrders = changeOrder.system_change_orders
  if (!systemChangeOrders) {
    return undefined
  }
  const entry = systemChangeOrders[systemId] ?? systemChangeOrders[String(systemId)]
  const content = entry?.change_order_content
  return content ?? undefined
}

/** Fallback chain: per-system content → template → payment contract template. */
export const resolveChangeOrderContractHtml = (
  changeOrder: ChangeOrderContractSource | null | undefined,
  systemId: number | null | undefined,
  paymentContractTemplate?: string | null
): string => {
  return (
    getChangeOrderContractContent(changeOrder, systemId) ??
    changeOrder?.change_order_template_content ??
    paymentContractTemplate ??
    ''
  )
}

/** Whether opening the Sale accordion should refetch server-authored sale / CO fields. */
export const shouldSyncSaleAccordionData = (
  projectValues: ChangeOrderProjectValues | undefined,
  changeOrderFeatureEnabled: boolean
): boolean => {
  if (!changeOrderFeatureEnabled || !projectValues?.id) {
    return false
  }
  return projectValues.project_sold != null && projectValues.project_sold !== undefined
}

export const fetchProjectSaleData = async (orgId: number, projectId: number): Promise<Record<string, unknown>> => {
  const requestUrl = `${window.API_ROOT}/api/orgs/${orgId}/projects/${projectId}/`
  const headers = window.Utils.tokenAuthHeaders({
    'Content-Type': 'application/json',
    'X-CSRFToken': window.getCookie('csrftoken') as string,
  })
  const request = new Request(requestUrl, {
    method: 'GET',
    headers: new Headers(headers),
  })
  const result = await fetchRequest(request, { format: 'json' })
  return result.json as Record<string, unknown>
}

const SALE_ONLY_SYNC_FIELDS = ['original_system_sold', 'system_sold', 'payment_option_sold'] as const

/** Preserve in-progress draft selections when refreshing from the server. */
export const mergeChangeOrdersFromServerResponse = (
  formChangeOrders: Array<ChangeOrderType | null | undefined> | undefined,
  serverChangeOrders: unknown
): ChangeOrderType[] => {
  const serverOrders = (Array.isArray(serverChangeOrders) ? serverChangeOrders : []).filter(
    Boolean
  ) as ChangeOrderType[]
  const formOrders = (formChangeOrders ?? []).filter(Boolean) as ChangeOrderType[]
  const activeFormOrder = formOrders.find(isActiveChangeOrder)

  if (!activeFormOrder) {
    return serverOrders
  }

  const matchingServerOrder = serverOrders.find((co) => co.id === activeFormOrder.id)
  if (!matchingServerOrder) {
    return formOrders
  }

  if (matchingServerOrder.is_complete || matchingServerOrder.status === ChangeOrderStatusEnum.COMPLETED) {
    return serverOrders
  }

  const mergedActiveOrder: ChangeOrderType = {
    ...matchingServerOrder,
    selected_systems:
      normalizeChangeOrderSelectedSystemIds(activeFormOrder.selected_systems).length > 0
        ? activeFormOrder.selected_systems
        : matchingServerOrder.selected_systems,
    document_template: activeFormOrder.document_template ?? matchingServerOrder.document_template,
  }

  return serverOrders.map((co) => (co.id === activeFormOrder.id ? mergedActiveOrder : co))
}

/** Merge server-authored sale / change-order fields into the project form. */
export const applySaleDataFromProjectResponse = (
  form: { getState: () => { values: Record<string, unknown> }; mutators: Record<string, unknown> },
  serverProject: Record<string, unknown>
): void => {
  const formValues = form.getState().values
  const { updateFieldSilently } = form.mutators as {
    updateFieldSilently: (name: string, value: unknown) => void
  }

  SALE_ONLY_SYNC_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(serverProject, field)) {
      updateFieldSilently(field, serverProject[field])
    }
  })

  if (Object.prototype.hasOwnProperty.call(serverProject, 'change_orders')) {
    const mergedChangeOrders = mergeChangeOrdersFromServerResponse(
      formValues.change_orders as Array<ChangeOrderType | null | undefined> | undefined,
      serverProject.change_orders
    )
    updateFieldSilently('change_orders', mergedChangeOrders)

    const hasActiveAfterMerge = mergedChangeOrders.some(isActiveChangeOrder)
    if (!hasActiveAfterMerge) {
      const serverInitiated = Object.prototype.hasOwnProperty.call(serverProject, 'initiated_change_order')
        ? serverProject.initiated_change_order
        : null
      updateFieldSilently('initiated_change_order', serverInitiated ?? null)
    }
  } else if (Object.prototype.hasOwnProperty.call(serverProject, 'initiated_change_order')) {
    const activeFormOrder = getInitiatedChangeOrder(formValues as ChangeOrderProjectValues)
    if (!activeFormOrder || serverProject.initiated_change_order != null) {
      updateFieldSilently('initiated_change_order', serverProject.initiated_change_order)
    }
  }
}

const getSystemIdForUuid = (uuid, projectValues) => {
  return projectValues.systems?.find((v) => v.uuid === uuid)?.id || null
}

export const useSystemIsEditable = (system, projectValues) => {
  if (!system || !projectValues) {
    return true
  }

  const hasInitiatedChangeOrder = hasActiveChangeOrder(projectValues)
  const previouslySoldIds = getPreviouslySoldSystemIds(projectValues)
  const currentSystemId =
    system.userData && system.userData.id ? system.userData.id : getSystemIdForUuid(system.userData.uuid, projectValues)
  const isCurrentSystemSold = previouslySoldIds.has(currentSystemId)
  return !(hasInitiatedChangeOrder && isCurrentSystemSold)
}
